import { Autocomplete, Chip, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTags } from '../../hooks/useWorkspace';

/** Suggested project categories (agreed: categories are plain tags). */
export const SUGGESTED_PROJECT_TAGS = ['zákazník', 'úrad-SK', 'úrad-CH', 'účtovníčka'];

type Props = {
    value: string[];
    onChange: (value: string[]) => void;
    label?: string;
    size?: 'small' | 'medium';
    suggestions?: string[];
    disabled?: boolean;
};

export const TagsInput = ({ value, onChange, label, size, suggestions = [], disabled }: Props) => {
    const { t } = useTranslation();
    const { data: tags = [] } = useTags();
    const options = Array.from(new Set([...suggestions, ...tags.map((tag) => tag.name)]));

    return (
        <Autocomplete
            multiple
            freeSolo
            disabled={disabled}
            size={size}
            options={options}
            value={value}
            filterSelectedOptions
            onChange={(_event, next) => {
                const cleaned = Array.from(new Set(next.map((item) => String(item).trim()).filter(Boolean)));
                onChange(cleaned);
            }}
            renderTags={(selected, getTagProps) =>
                selected.map((option, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    return <Chip key={key} size="small" label={option} {...rest} />;
                })
            }
            renderInput={(params) => (
                <TextField {...params} label={label ?? t('tags.title')} placeholder={value.length ? '' : t('tags.placeholder')} />
            )}
        />
    );
};

export const TagChips = ({ tags, max = 4, onClick }: {
    tags?: Array<{ id?: string; name: string; color?: string | null }>;
    max?: number;
    onClick?: (name: string) => void;
}) => {
    if (!tags || tags.length === 0) {
        return <span style={{ color: '#999' }}>—</span>;
    }
    const visible = tags.slice(0, max);
    return (
        <>
            {visible.map((tag) => (
                <Chip
                    key={tag.id ?? tag.name}
                    size="small"
                    label={tag.name}
                    onClick={onClick ? (event) => { event.stopPropagation(); onClick(tag.name); } : undefined}
                    sx={{ mr: 0.5, mb: 0.25, ...(tag.color ? { bgcolor: tag.color, color: '#fff' } : {}) }}
                />
            ))}
            {tags.length > max && <Chip size="small" variant="outlined" label={`+${tags.length - max}`} />}
        </>
    );
};
